import { useCallback, useEffect, useRef, useState } from "react";
import { SourceReference } from "~/api/model";
import { getStoredAccessToken, useChatDocumentParams } from "~/helpers";
import { BASE_API_URL } from "~/utils/apiUrl";

type SSEEventType =
  | "message_start"
  | "thinking_start"
  | "thinking_end"
  | "delta"
  | "tool_call"
  | "tool_call_start"
  | "tool_call_delta"
  | "fallback"
  | "message_end"
  | "error"
  | "comprehensive_progress"
  | "async_report_scheduled"
  | "link_fetch_progress";

interface SSEDeltaEvent {
  content: string;
}

interface SSEMessageEndEvent {
  content: string;
  sources?: SourceReference[];
  messageId: string;
}

interface SSEErrorEvent {
  message: string;
  recoverable: boolean;
}

interface SSEThinkingStartEvent {
  mode: "off" | "on" | "auto";
  ts: number;
}

interface SSEComprehensiveProgressEvent {
  stage: string;
  message: string;
}

interface SSEAsyncReportScheduledEvent {
  messageId: string;
  jobId: string;
}

interface SSELinkFetchProgressEvent {
  urls: string[];
  status: "fetching" | "complete";
  fetchedCount?: number;
}

function isSSEAsyncReportScheduledEvent(
  data: unknown
): data is SSEAsyncReportScheduledEvent {
  return (
    typeof data === "object" &&
    data !== null &&
    "messageId" in data &&
    typeof (data as SSEAsyncReportScheduledEvent).messageId === "string" &&
    "jobId" in data &&
    typeof (data as SSEAsyncReportScheduledEvent).jobId === "string"
  );
}

function isSSELinkFetchProgressEvent(
  data: unknown
): data is SSELinkFetchProgressEvent {
  return (
    typeof data === "object" &&
    data !== null &&
    "urls" in data &&
    Array.isArray((data as SSELinkFetchProgressEvent).urls) &&
    "status" in data &&
    typeof (data as SSELinkFetchProgressEvent).status === "string"
  );
}

/**
 * Runtime type guard for SSEComprehensiveProgressEvent.
 * Validates the shape before use so malformed server data is
 * silently skipped instead of causing a runtime crash.
 */
function isSSEComprehensiveProgressEvent(
  data: unknown
): data is SSEComprehensiveProgressEvent {
  return (
    typeof data === "object" &&
    data !== null &&
    "stage" in data &&
    typeof (data as SSEComprehensiveProgressEvent).stage === "string" &&
    "message" in data &&
    typeof (data as SSEComprehensiveProgressEvent).message === "string"
  );
}

interface UseSSEChatMessageOptions {
  onDelta?: (content: string) => void;
  onMessageEnd?: (event: SSEMessageEndEvent) => void;
  onError?: (error: SSEErrorEvent) => void;
  onThinkingStart?: (event: SSEThinkingStartEvent) => void;
  onThinkingEnd?: () => void;
  onComprehensiveProgress?: (event: SSEComprehensiveProgressEvent) => void;
  onAsyncReportScheduled?: (event: SSEAsyncReportScheduledEvent) => void;
  onLinkFetchProgress?: (event: SSELinkFetchProgressEvent) => void;
}

interface ChatImagePayload {
  image: string;
  imageMimeType: string;
}

interface UseSSEChatMessageReturn {
  sendMessage: (message: string, attachment?: ChatImagePayload | null) => void;
  cancelStream: () => void;
  isStreaming: boolean;
  isThinking: boolean;
  isComprehensiveProcessing: boolean;
  comprehensiveStage: string;
  isFetchingLinks: boolean;
  linkFetchUrls: string[];
  streamingContent: string;
  error: string | null;
}

/** Maximum number of automatic retries for network errors during comprehensive processing. */
const MAX_COMPREHENSIVE_RETRIES = 1;

/** Delay in ms before retrying after a network error during comprehensive processing. */
const COMPREHENSIVE_RETRY_DELAY_MS = 2_000;

export const useSSEChatMessage = (
  options?: UseSSEChatMessageOptions
): UseSSEChatMessageReturn => {
  const { organizationId, caseId, chatId } = useChatDocumentParams();
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isComprehensiveProcessing, setIsComprehensiveProcessing] =
    useState(false);
  const [comprehensiveStage, setComprehensiveStage] = useState("");
  const [isFetchingLinks, setIsFetchingLinks] = useState(false);
  const [linkFetchUrls, setLinkFetchUrls] = useState<string[]>([]);
  const [streamingContent, setStreamingContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setIsThinking(false);
    setIsComprehensiveProcessing(false);
    setComprehensiveStage("");
    setIsFetchingLinks(false);
    setLinkFetchUrls([]);
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const sendMessage = useCallback(
    async (message: string, attachment?: ChatImagePayload | null) => {
      if (isStreaming) {
        return;
      }

      setIsStreaming(true);
      setStreamingContent("");
      setError(null);

      /**
       * Execute one streaming attempt. Resolves when the stream
       * completes normally; throws on network / fetch errors so the
       * caller can decide whether to retry.
       */
      const attemptStream = async (): Promise<void> => {
        abortControllerRef.current = new AbortController();

        const response = await fetch(
          `${BASE_API_URL}/organizations/${organizationId}/projects/${caseId}/chat/${chatId}/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getStoredAccessToken()}`,
            },
            body: JSON.stringify(
              attachment
                ? {
                    message,
                    image: attachment.image,
                    imageMimeType: attachment.imageMimeType,
                  }
                : { message }
            ),
            signal: abortControllerRef.current.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulatedContent = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;

            // SSE comments (lines starting with ':') are keepalive
            // signals from the server — skip them.
            if (eventBlock.startsWith(":")) continue;

            const lines = eventBlock.split("\n");
            let currentEvent: SSEEventType | "" = "";
            let dataLines: string[] = [];

            for (const line of lines) {
              if (line.startsWith("event: ")) {
                currentEvent = line.slice(7) as SSEEventType;
              } else if (line.startsWith("data: ")) {
                dataLines.push(line.slice(6));
              }
            }

            if (currentEvent && dataLines.length > 0) {
              try {
                const dataStr = dataLines.join("\n");
                const data = JSON.parse(dataStr);

                switch (currentEvent) {
                  case "thinking_start": {
                    const thinkingEvent = data as SSEThinkingStartEvent;
                    setIsThinking(true);
                    options?.onThinkingStart?.(thinkingEvent);
                    break;
                  }
                  case "thinking_end": {
                    setIsThinking(false);
                    options?.onThinkingEnd?.();
                    break;
                  }
                  case "delta": {
                    const deltaEvent = data as SSEDeltaEvent;
                    accumulatedContent += deltaEvent.content || "";
                    setStreamingContent(accumulatedContent);
                    options?.onDelta?.(deltaEvent.content || "");
                    break;
                  }
                  case "message_end": {
                    const endEvent = data as SSEMessageEndEvent;
                    setError(null);
                    options?.onMessageEnd?.(endEvent);
                    break;
                  }
                  case "comprehensive_progress": {
                    if (isSSEComprehensiveProgressEvent(data)) {
                      setIsComprehensiveProcessing(true);
                      setComprehensiveStage(data.message);
                      options?.onComprehensiveProgress?.(data);
                    }
                    break;
                  }
                  case "async_report_scheduled": {
                    if (isSSEAsyncReportScheduledEvent(data)) {
                      options?.onAsyncReportScheduled?.(data);
                    }
                    break;
                  }
                  case "link_fetch_progress": {
                    if (isSSELinkFetchProgressEvent(data)) {
                      setIsFetchingLinks(data.status === "fetching");
                      setLinkFetchUrls(data.urls);
                      options?.onLinkFetchProgress?.(data);
                    }
                    break;
                  }
                  case "error": {
                    const errorEvent = data as SSEErrorEvent;
                    setError(errorEvent.message);
                    options?.onError?.(errorEvent);
                    break;
                  }
                }
              } catch {
                // Ignore parse errors for malformed SSE data
              }
            }
          }
        }
      };

      try {
        for (let attempt = 0; attempt <= MAX_COMPREHENSIVE_RETRIES; attempt++) {
          try {
            await attemptStream();
            // Stream completed normally — no retry needed
            break;
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
              // Stream was cancelled by user — do not retry
              return;
            }

            const isLastAttempt = attempt === MAX_COMPREHENSIVE_RETRIES;
            if (isLastAttempt) {
              throw err;
            }

            // Retry: clear stale content from the failed attempt, wait
            // briefly, then loop.
            setStreamingContent("");
            setComprehensiveStage("Reconnecting…");
            await new Promise((resolve) =>
              setTimeout(resolve, COMPREHENSIVE_RETRY_DELAY_MS)
            );

            // If the user cancelled while we were waiting, stop retrying.
            if (!abortControllerRef.current) {
              return;
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Stream was cancelled by user
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : "Failed to send message";
        setError(errorMessage);
        options?.onError?.({ message: errorMessage, recoverable: true });
      } finally {
        setIsStreaming(false);
        setIsThinking(false);
        setIsComprehensiveProcessing(false);
        setComprehensiveStage("");
        setIsFetchingLinks(false);
        setLinkFetchUrls([]);
        abortControllerRef.current = null;
      }
    },
    [organizationId, caseId, chatId, isStreaming, options]
  );

  return {
    sendMessage,
    cancelStream,
    isStreaming,
    isThinking,
    isComprehensiveProcessing,
    comprehensiveStage,
    isFetchingLinks,
    linkFetchUrls,
    streamingContent,
    error,
  };
};
